import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Plus, Trash2, Power, Loader2, Tag, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AiSettingsCard = ({ token }) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/ai-settings`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(setSettings).catch(() => {});
  }, [token]);

  const toggle = async (field) => {
    const next = { ...settings, [field]: !settings[field] };
    setSettings(next);
    await fetch(`${API_URL}/api/admin/ai-settings`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: next[field] })
    });
  };

  if (!settings) return null;

  const Toggle = ({ field, label, desc, testId }) => (
    <div className="flex items-center justify-between gap-3 py-2">
      <div>
        <p className="text-white text-sm font-medium">{label}</p>
        <p className="text-gray-500 text-xs">{desc}</p>
      </div>
      <button
        onClick={() => toggle(field)}
        data-testid={testId}
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${settings[field] ? 'bg-cyan-600' : 'bg-zinc-700'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${settings[field] ? 'left-[22px]' : 'left-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-4 md:p-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
          <Zap className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-white font-bold text-base md:text-lg">Intelligence Artificielle (Gemini)</h2>
          <p className="text-gray-400 text-xs">
            {settings.key_configured ? 'Clé Gemini configurée ✓ — quota gratuit Google' : '⚠️ Clé GEMINI_API_KEY manquante dans le .env backend'}
          </p>
        </div>
      </div>
      <div className="divide-y divide-zinc-800">
        <Toggle field="discord_enabled" label="IA dans les tickets Discord" desc="Répond automatiquement aux questions des clients (les mots-clés restent prioritaires)" testId="ai-toggle-discord" />
        <Toggle field="site_enabled" label="Chatbot sur le site web" desc="Bulle de chat en bas à droite pour les visiteurs" testId="ai-toggle-site" />
      </div>
    </div>
  );
};

const FaqBotTab = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [keywords, setKeywords] = useState('');
  const [title, setTitle] = useState('');
  const [response, setResponse] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchEntries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/faq`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setEntries(await res.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    const kwList = keywords.split(',').map(k => k.trim()).filter(Boolean);
    if (kwList.length === 0 || !response.trim()) {
      setError('Mots-clés et réponse requis');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/faq`, {
        method: 'POST', headers,
        body: JSON.stringify({ keywords: kwList, title: title.trim() || 'Réponse automatique', response: response.trim() })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      setKeywords(''); setTitle(''); setResponse('');
      fetchEntries();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (entry) => {
    await fetch(`${API_URL}/api/admin/faq/${entry.id}`, {
      method: 'PUT', headers, body: JSON.stringify({ enabled: !entry.enabled })
    });
    setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, enabled: !e.enabled } : e));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette réponse automatique ?')) return;
    await fetch(`${API_URL}/api/admin/faq/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="faq-bot-tab">
      <AiSettingsCard token={token} />
      <div className="bg-zinc-900/50 border border-amber-900/30 rounded-xl p-4 md:p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Bot className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-base md:text-lg">Bot FAQ — Réponses automatiques</h2>
            <p className="text-gray-400 text-xs">Le bot répond dans les tickets Discord quand un client tape un mot-clé. Cooldown de 10 min par réponse/ticket.</p>
          </div>
        </div>

        <form onSubmit={handleCreate} className="mt-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs font-medium mb-1 block">Mots-clés (séparés par des virgules)</label>
              <input
                value={keywords} onChange={(e) => setKeywords(e.target.value)}
                placeholder="délai, combien de temps, durée"
                data-testid="faq-keywords-input"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium mb-1 block">Titre (optionnel)</label>
              <input
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Délais de livraison"
                data-testid="faq-title-input"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs font-medium mb-1 block">Réponse du bot</label>
            <textarea
              value={response} onChange={(e) => setResponse(e.target.value)}
              rows={3}
              placeholder="Ton optimisation est livrée en moins de 30 minutes après la prise en charge..."
              data-testid="faq-response-input"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500 resize-y"
            />
          </div>
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button
            type="submit" disabled={saving}
            data-testid="faq-create-btn"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Ajouter la réponse
          </button>
        </form>
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 text-amber-500 animate-spin mx-auto" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-10 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <Bot className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">Aucune réponse automatique configurée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <div key={entry.id} data-testid={`faq-entry-${entry.id.slice(0, 8)}`} className={`bg-zinc-900/50 border rounded-xl p-4 transition-colors ${entry.enabled ? 'border-zinc-800' : 'border-zinc-800 opacity-50'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <h3 className="text-white font-semibold text-sm">{entry.title}</h3>
                    {!entry.enabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-gray-500">Désactivé</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {entry.keywords.map((kw, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        <Tag className="w-2.5 h-2.5" />{kw}
                      </span>
                    ))}
                  </div>
                  <p className="text-gray-400 text-xs whitespace-pre-wrap line-clamp-3">{entry.response}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleEnabled(entry)}
                    data-testid={`faq-toggle-${entry.id.slice(0, 8)}`}
                    title={entry.enabled ? 'Désactiver' : 'Activer'}
                    className={`p-2 rounded-lg transition-colors ${entry.enabled ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25' : 'bg-zinc-800 text-gray-500 hover:text-white'}`}
                  >
                    <Power className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    data-testid={`faq-delete-${entry.id.slice(0, 8)}`}
                    className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FaqBotTab;
